import { useEffect, useState } from "react";
import DataTable, { Column } from "@/components/ui/DataTable";
import { ButtonAdd, ButtonDelete, ButtonEdit } from "@/components/ui/button";
import {
  useDeleteIngredientMutation,
  useGetIngredientsQuery,
} from "@/redux/services/inventory/ingredient.api";
import { Ingredient } from "@/redux/services/inventory/inventory.type";
import { displayPrice, formatStock } from "@/lib/utils";
import { useModal, usePagination, useRestaurantCurrency } from "@/lib/hooks";
import { useDebounce } from "@/hooks/useDebounce";
import { usePermissions } from "@/hooks/usePermissions";
import IngredientFormModal from "./IngredientFormModal";
import StockAdjustmentModal from "./StockAdjustmentModal";
import IngredientDetailsDrawer from "./IngredientDetailsDrawer";
import { useConfirm } from "@/components/ui/confirm-dialog";
import toast from "react-hot-toast";
import { PlusCircle, Trash2 } from "lucide-react";

interface IngredientsListProps {
  restaurantId: string;
  showLowStockOnly?: boolean;
  onToggleLowStock?: () => void;
}

const IngredientsList = ({
  restaurantId,
  showLowStockOnly = false,
  onToggleLowStock,
}: IngredientsListProps) => {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [adjustIngredient, setAdjustIngredient] = useState<Ingredient | null>(
    null
  );
  const [drawerIngredientId, setDrawerIngredientId] = useState<string | null>(
    null
  );
  const { page, limit, onChange } = usePagination(25);
  const currency = useRestaurantCurrency();
  const { canManage } = usePermissions();
  const canManageInventory = canManage("inventory");

  useEffect(() => {
    // Reset to first page whenever the filter surface changes
    onChange(limit, 1);
  }, [debouncedSearch, showLowStockOnly]);

  const { data, isFetching } = useGetIngredientsQuery({
    restaurantId,
    query: {
      page,
      limit,
      ...(debouncedSearch.trim() ? { name: debouncedSearch.trim() } : {}),
      ...(showLowStockOnly ? { lowStock: true } : {}),
    },
  });

  const {
    isOpen: isFormOpen,
    item: ingredientToEdit,
    closeModal: closeFormModal,
    openModal: openFormModal,
    openEditModal,
  } = useModal<Ingredient>();

  const [deleteIngredient] = useDeleteIngredientMutation();
  const confirm = useConfirm();

  const handleDelete = async (ingredient: Ingredient) => {
    const ok = await confirm({
      title: `Supprimer "${ingredient.name}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      const res = await deleteIngredient({
        restaurantId,
        ingredientUuid: ingredient.id,
      });
      if ("error" in res) {
        toast.error("Erreur lors de la suppression");
        return;
      }
      toast.success("Ingrédient supprimé avec succès");
    } catch {
      toast.error("Une erreur est survenue");
    }
  };

  const columns: Column[] = [
    {
      key: "name",
      label: "Nom",
      formatter: (_v, row: Ingredient) => (
        <span className="font-medium text-gray-900">{row.name}</span>
      ),
    },
    { key: "baseUnit", label: "Unité" },
    {
      key: "availableStock",
      label: "Disponible",
      formatter: (_v, row: Ingredient) => {
        const reserved = parseFloat(row.reservedStock) || 0;
        return (
          <div>
            <div className="font-medium text-gray-900">
              {formatStock(row.availableStock, row.baseUnit)}
            </div>
            {reserved > 0 && (
              <div className="text-xs text-gray-500">
                {formatStock(row.currentStock, row.baseUnit)} total
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "lowStockThreshold",
      label: "Seuil",
      formatter: (_v, row: Ingredient) =>
        formatStock(row.lowStockThreshold, row.baseUnit),
    },
    {
      key: "costPerUnit",
      label: "Coût",
      formatter: (_v, row: Ingredient) =>
        row.costPerUnit > 0 ? (
          <>
            {displayPrice(row.costPerUnit, currency)}
            <span className="text-xs text-gray-500 ml-1">/ {row.baseUnit}</span>
          </>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: "inventoryValue",
      label: "Valeur",
      formatter: (_v, row: Ingredient) =>
        row.inventoryValue > 0 ? (
          displayPrice(row.inventoryValue, currency)
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: "lowStock",
      label: "Statut",
      formatter: (_v, row: Ingredient) =>
        row.lowStock ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            Stock faible
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            OK
          </span>
        ),
    },
    {
      key: "actions",
      label: "Actions",
      formatter: (_v, row: Ingredient) => (
        <div className="flex items-center gap-1">
          {canManageInventory && (
            <>
              <ButtonEdit onClick={() => openEditModal(row)} />
              <button
                type="button"
                title="Ajuster le stock"
                className="h-7 w-7 rounded-full flex items-center justify-center bg-blue-100 hover:bg-blue-200 transition-all"
                onClick={() => setAdjustIngredient(row)}
              >
                <PlusCircle className="h-4 w-4 text-blue-600" />
              </button>
              <ButtonDelete onClick={() => handleDelete(row)} />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {onToggleLowStock && (
            <button
              type="button"
              onClick={onToggleLowStock}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                showLowStockOnly
                  ? "bg-orange-100 text-orange-700 border border-orange-300"
                  : "bg-muted text-muted-foreground hover:bg-muted"
              }`}
            >
              Stock faible
            </button>
          )}
          {canManageInventory && (
            <ButtonAdd
              label="Ajouter un ingrédient"
              type="button"
              onClick={openFormModal}
            />
          )}
        </div>

        <DataTable
          data={data?.results ?? []}
          columns={columns}
          loading={isFetching}
          totalCount={data?.count ?? 0}
          currentPage={page}
          pageSize={limit}
          searchValue={searchInput}
          onSearch={setSearchInput}
          onPageChange={(p) => onChange(limit, p)}
          onPageSizeChange={(size) => onChange(size, 1)}
          onRowClick={(row: Ingredient) => setDrawerIngredientId(row.id)}
          emptyMessage={
            searchInput || showLowStockOnly
              ? "Aucun résultat pour votre recherche"
              : "Aucun ingrédient"
          }
        />
      </div>

      {isFormOpen && (
        <IngredientFormModal
          isOpen={isFormOpen}
          onClose={closeFormModal}
          ingredientToEdit={ingredientToEdit}
          restaurantId={restaurantId}
        />
      )}

      {adjustIngredient && (
        <StockAdjustmentModal
          isOpen={!!adjustIngredient}
          onClose={() => setAdjustIngredient(null)}
          ingredient={adjustIngredient}
        />
      )}

      <IngredientDetailsDrawer
        isOpen={!!drawerIngredientId}
        onClose={() => setDrawerIngredientId(null)}
        restaurantId={restaurantId}
        ingredientId={drawerIngredientId}
      />
    </>
  );
};

export default IngredientsList;
